import { type CSSProperties, type ReactElement } from 'react';
import styles from './distribution-key-figures.module.scss';
import { Font } from '@utmb/design-system/text/font';
import { Icon } from '@utmb/design-system/icons/Icon';
import { getCategoryColor, getColorStyle } from '@utmb/utils/colors';
import { ColorTypesEnum } from '@utmb/types/colors';
import { IconsEnum } from '@utmb/types/icons';
import { PiCategoryEnum } from '@utmb/types/enum/PiCategoryEnum';
import { useTranslation } from 'next-i18next';

export interface DistributionKeyFiguresProps {
    rank: number | null;
    totalRanked: number;
    topPercentage: number | null;
    category: PiCategoryEnum;
}

export function DistributionKeyFigures({ rank, totalRanked, topPercentage, category }: DistributionKeyFiguresProps): ReactElement {
    const { t } = useTranslation();

    const categoryColor = category === PiCategoryEnum.GENERAL ? getColorStyle(ColorTypesEnum.WS_SECONDARY) : getCategoryColor(category);

    const hasRank = rank !== null && rank > 0 && totalRanked > 0;
    const hasTop = topPercentage !== null;
    const hasData = hasRank || hasTop;

    return (
        <div className={styles.distributionKeyFigures} style={{ ['--category-color' as string]: categoryColor } as CSSProperties}>
            {hasRank && (
                <div className={styles.bigValue}>
                    <div className={styles.rankRow}>
                        <Font.OxaniumRegular desktop={String(rank).length > 5 ? '45' : '64'} mobile="32" className={styles.rank}>
                            {rank}
                        </Font.OxaniumRegular>
                        <Font.OxaniumRegular desktop="32" mobile="20" className={styles.totalRanked}>
                            / {totalRanked}
                        </Font.OxaniumRegular>
                    </div>
                </div>
            )}

            {hasTop && (
                <div className={styles.bigValue}>
                    <Font.FuturaBook color={getColorStyle(ColorTypesEnum.WHITE)} desktop="24" mobile="16" className={styles.label}>
                        {t('singleRunner.statistics.global.top')}
                    </Font.FuturaBook>
                    <Font.OxaniumRegular desktop="96" mobile="40" className={styles.topValue}>
                        {Math.max(topPercentage as number, 1)}%
                    </Font.OxaniumRegular>
                </div>
            )}

            {!hasData && (
                <div className={styles.noData}>
                    <div className={styles.lock}>
                        <Icon variant={IconsEnum.LOCK} color={getColorStyle(ColorTypesEnum.BLUE_LIGHT)} />
                    </div>
                    <Font.FuturaMedium className={styles.noDataLabel} desktop="16" mobile="14" color={getColorStyle(ColorTypesEnum.WHITE)}>
                        {t('singleRunner.statistics.indexNoValid')}
                    </Font.FuturaMedium>
                </div>
            )}
        </div>
    );
}
